"use client";

import { useCallback, useMemo, useState } from "react";
import {
  PRODUCT_CONFIGS,
  type DimensionPreset,
  type ProductTypeConfig,
  type TruckLoadingProductSlug,
} from "@/lib/calculators/truck-loading-merged/product-presets";
import {
  findPreset,
  presetStackable,
  presetUnitLabels,
  presetWeightLbs,
} from "@/lib/calculators/truck-loading-merged/preset-values";
import { TRUCK_SPECS } from "@/lib/calculators/truck-loading-merged/truck-specs";
import {
  calculateFit,
  findBestLoadingType,
} from "@/lib/calculators/truck-loading-merged/calculate-fit";
import { computeItemPositions } from "@/lib/calculators/truck-loading-merged/compute-positions";
import {
  toNumber,
  isCylindrical,
  DEFAULT_PALLET_HEIGHT_IN,
} from "@/lib/calculators/truck-loading-merged/types";
import type {
  TruckSpec,
  ItemDimensions,
  GeometryCategory,
  LoadingType,
} from "@/lib/calculators/truck-loading-merged/types";
import { useVirtualTruck } from "./use-virtual-truck";
import { useOptimizedLoading } from "./use-optimized-loading";
import type { AdditionalItem } from "./additional-item-types";

export type { AdditionalItem } from "./additional-item-types";

function useSyncedValue(computed: number) {
  const [value, setValue] = useState(computed);
  const [prevComputed, setPrevComputed] = useState(computed);
  if (computed !== prevComputed) {
    setPrevComputed(computed);
    setValue(computed);
  }
  return [value, setValue] as const;
}

let additionalItemCounter = 0;

function useAdditionalItems() {
  const [additionalItems, setAdditionalItems] = useState<Array<AdditionalItem>>(
    [],
  );

  const addItem = useCallback(() => {
    additionalItemCounter += 1;
    setAdditionalItems((previous) => [
      ...previous,
      {
        id: `add-${additionalItemCounter}`,
        presetId: "",
        length: "",
        width: "",
        height: "",
        weight: "",
        quantity: "1",
        stackable: false,
      },
    ]);
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setAdditionalItems((previous) =>
      previous.filter((existingItem) => existingItem.id !== itemId),
    );
  }, []);

  const updateItem = useCallback(
    (itemId: string, updates: Partial<Omit<AdditionalItem, "id">>) => {
      setAdditionalItems((previous) =>
        previous.map((existingItem) =>
          existingItem.id === itemId
            ? { ...existingItem, ...updates }
            : existingItem,
        ),
      );
    },
    [],
  );

  const clearItems = useCallback(() => setAdditionalItems([]), []);

  return { additionalItems, addItem, removeItem, updateItem, clearItems };
}

function weightInput(config: ProductTypeConfig, preset: DimensionPreset) {
  const weightLbs = presetWeightLbs(config, preset);
  return weightLbs > 0 ? String(weightLbs) : "";
}

function dimensionChangeHandlers(
  setters: Record<"length" | "width" | "height", (value: string) => void>,
  setPresetId: (presetId: string) => void,
) {
  function makeDimensionHandler(setter: (value: string) => void) {
    return (value: string) => {
      setter(value);
      setPresetId("custom");
    };
  }

  return {
    length: makeDimensionHandler(setters.length),
    width: makeDimensionHandler(setters.width),
    height: makeDimensionHandler(setters.height),
  };
}

function useDimensionInputs(config: ProductTypeConfig) {
  const defaultPreset = config.presets[0];
  const [presetId, setPresetId] = useState(defaultPreset.id);
  const [length, setLength] = useState(String(defaultPreset.lengthIn));
  const [width, setWidth] = useState(String(defaultPreset.widthIn));
  const [height, setHeight] = useState(String(defaultPreset.heightIn));
  const [weight, setWeight] = useState(weightInput(config, defaultPreset));

  function applyPreset(presetConfig: ProductTypeConfig, preset: DimensionPreset) {
    setPresetId(preset.id);
    setLength(String(preset.lengthIn));
    setWidth(String(preset.widthIn));
    setHeight(String(preset.heightIn));
    setWeight(weightInput(presetConfig, preset));
  }

  function handlePresetChange(newPresetId: string) {
    const preset = findPreset(config, newPresetId);
    if (preset?.id === "custom") setPresetId(preset.id);
    else if (preset) applyPreset(config, preset);
  }

  const handleDimensionChange = dimensionChangeHandlers(
    { length: setLength, width: setWidth, height: setHeight },
    setPresetId,
  );

  function resetDimensions(newConfig: ProductTypeConfig) {
    applyPreset(newConfig, newConfig.presets[0]);
  }

  return {
    presetId,
    length,
    width,
    height,
    weight,
    setWeight,
    handlePresetChange,
    handleDimensionChange,
    resetDimensions,
  };
}

function defaultStackable(config: ProductTypeConfig): boolean {
  return presetStackable(config, config.presets[0]);
}

function useLoadingState(productType: TruckLoadingProductSlug) {
  const config = PRODUCT_CONFIGS[productType];

  const [selectedTruck, setSelectedTruck] = useState<TruckSpec>(TRUCK_SPECS[0]);
  const [geometryCategory, setGeometryCategory] = useState<GeometryCategory>(
    config.defaultGeometry,
  );
  const [stackable, setStackable] = useState(defaultStackable(config));
  const [palletized, setPalletized] = useState(false);
  const [palletHeight, setPalletHeight] = useState(
    String(DEFAULT_PALLET_HEIGHT_IN),
  );
  const [loadingType, setLoadingType] = useState<LoadingType>("straight");
  const [specificQtyEnabled, setSpecificQtyEnabled] = useState(false);
  const [targetQty, setTargetQty] = useState("");

  const resetForProductChange = useCallback(
    (newConfig: ProductTypeConfig) => {
      setGeometryCategory(newConfig.defaultGeometry);
      setStackable(defaultStackable(newConfig));
      setPalletized(false);
      setPalletHeight(String(DEFAULT_PALLET_HEIGHT_IN));
      setLoadingType("straight");
      setSpecificQtyEnabled(false);
      setTargetQty("");
    },
    [],
  );

  return {
    selectedTruck,
    setSelectedTruck,
    geometryCategory,
    setGeometryCategory,
    stackable,
    setStackable,
    palletized,
    setPalletized,
    palletHeight,
    setPalletHeight,
    loadingType,
    setLoadingType,
    specificQtyEnabled,
    setSpecificQtyEnabled,
    targetQty,
    setTargetQty,
    resetForProductChange,
  };
}

export function useCalculatorState(productType: TruckLoadingProductSlug) {
  const config = PRODUCT_CONFIGS[productType];
  const loadingState = useLoadingState(productType);
  const dimensions = useDimensionInputs(config);
  const { additionalItems, addItem, removeItem, updateItem, clearItems } =
    useAdditionalItems();

  const [prevProductType, setPrevProductType] =
    useState<TruckLoadingProductSlug>(productType);

  if (productType !== prevProductType) {
    setPrevProductType(productType);
    const newConfig = PRODUCT_CONFIGS[productType];
    dimensions.resetDimensions(newConfig);
    loadingState.resetForProductChange(newConfig);
    clearItems();
  }

  const item: ItemDimensions = useMemo(
    () => ({
      lengthIn: Math.max(toNumber(dimensions.length), 0),
      widthIn: Math.max(toNumber(dimensions.width), 0),
      heightIn: Math.max(toNumber(dimensions.height), 0),
      weightLbs: Math.max(toNumber(dimensions.weight), 0),
      geometryCategory: loadingState.geometryCategory,
      stackable: loadingState.stackable,
      palletized: loadingState.palletized,
      palletHeightIn: loadingState.palletized
        ? Math.max(toNumber(loadingState.palletHeight), 0)
        : 0,
    }),
    [
      dimensions.length,
      dimensions.width,
      dimensions.height,
      dimensions.weight,
      loadingState.geometryCategory,
      loadingState.stackable,
      loadingState.palletized,
      loadingState.palletHeight,
    ],
  );

  const isCylindricalItem = isCylindrical(item);
  const virtualTruck = useVirtualTruck(
    loadingState.selectedTruck,
    additionalItems,
  );

  const bestLoadingType = useMemo(
    () =>
      isCylindricalItem ? "straight" : findBestLoadingType(virtualTruck, item),
    [virtualTruck, item, isCylindricalItem],
  );

  const [prevBestType, setPrevBestType] = useState(bestLoadingType);
  if (bestLoadingType !== prevBestType) {
    setPrevBestType(bestLoadingType);
    loadingState.setLoadingType(bestLoadingType);
  }

  const result = useMemo(
    () => calculateFit(virtualTruck, item, loadingState.loadingType),
    [virtualTruck, item, loadingState.loadingType],
  );

  const optimizedLoading = useOptimizedLoading(
    loadingState.specificQtyEnabled,
    loadingState.targetQty,
    result,
    item.weightLbs,
  );

  const [perFloor, setPerFloor] = useSyncedValue(optimizedLoading.perFloor);
  const [stackLayers, setStackLayers] = useSyncedValue(
    optimizedLoading.stackLayers,
  );

  const positions = useMemo(
    () =>
      computeItemPositions(
        virtualTruck,
        item,
        perFloor,
        stackLayers,
        loadingState.loadingType,
      ).slice(0, result.totalFits),
    [
      virtualTruck,
      item,
      perFloor,
      stackLayers,
      loadingState.loadingType,
      result.totalFits,
    ],
  );

  return {
    config,
    selectedTruck: loadingState.selectedTruck,
    setSelectedTruck: loadingState.setSelectedTruck,
    presetId: dimensions.presetId,
    geometryCategory: loadingState.geometryCategory,
    setGeometryCategory: loadingState.setGeometryCategory,
    length: dimensions.length,
    width: dimensions.width,
    height: dimensions.height,
    weight: dimensions.weight,
    setWeight: dimensions.setWeight,
    stackable: loadingState.stackable,
    setStackable: loadingState.setStackable,
    palletized: loadingState.palletized,
    setPalletized: loadingState.setPalletized,
    palletHeight: loadingState.palletHeight,
    setPalletHeight: loadingState.setPalletHeight,
    unitLabels: presetUnitLabels(config, findPreset(config, dimensions.presetId)),
    handlePresetChange: (newPresetId: string) => {
      dimensions.handlePresetChange(newPresetId);
      const preset = findPreset(config, newPresetId);
      if (preset && preset.id !== "custom") {
        loadingState.setStackable(presetStackable(config, preset));
      }
    },
    handleDimensionChange: dimensions.handleDimensionChange,
    item,
    loadingType: loadingState.loadingType,
    setLoadingType: loadingState.setLoadingType,
    bestLoadingType,
    isCylindricalItem,
    result,
    perFloor,
    setPerFloor,
    stackLayers,
    setStackLayers,
    positions,
    virtualTruck,
    hasWeight: item.weightLbs > 0,
    specificQtyEnabled: loadingState.specificQtyEnabled,
    setSpecificQtyEnabled: loadingState.setSpecificQtyEnabled,
    targetQty: loadingState.targetQty,
    setTargetQty: loadingState.setTargetQty,
    additionalItems,
    addItem,
    removeItem,
    updateItem,
  };
}
